import type { QualifyingWindow, TierStatus } from "../types";
import type { RebateTierTranslatedContent } from "../useRebateTierTranslatedContent";

import { Typo } from "../../Typo";
import { formatCycleEnd } from "./formatCycleEnd";
import { LabelledValue } from "./LabelledValue";
import { SheetNotice } from "./SheetNotice";

export type CycleCardProps = {
  status: TierStatus;
  cycleEndsAt: string | null;
  qualifyingWindow: QualifyingWindow | null;
  content: RebateTierTranslatedContent;
  onOpenCriteria: () => void;
};

/** `YYYY-MM` → the first day of that month, e.g. "1 May 2026". */
const formatWindowStart = (month: string): string => {
  const [year, index] = month.split("-").map(Number);
  return new Date(Date.UTC(year, index - 1, 1)).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

/** `YYYY-MM` → the last day of that month, e.g. "31 Jul 2026". */
const formatWindowEnd = (month: string): string => {
  const [year, index] = month.split("-").map(Number);
  return new Date(Date.UTC(year, index, 0)).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

export const CycleCard = ({
  status,
  cycleEndsAt,
  qualifyingWindow,
  content,
  onOpenCriteria,
}: CycleCardProps) => {
  const notice =
    status === "grace"
      ? content.graceNotice
      : status === "frozen"
        ? content.frozenNotice
        : null;

  return (
    <div className="flex flex-col gap-6">
      {notice && <SheetNotice message={notice} />}

      {cycleEndsAt && (
        <LabelledValue
          label={content.cycleEnds}
          value={formatCycleEnd(cycleEndsAt)}
        />
      )}

      {qualifyingWindow && (
        <LabelledValue
          label={content.qualifyingWindow}
          value={`${formatWindowStart(qualifyingWindow.from)} – ${formatWindowEnd(qualifyingWindow.to)}`}
        />
      )}

      <Typo element="p" type="bodySM" textColor="text-text-primary">
        {content.cycleBody + " "}
        <span
          className="cursor-pointer text-text-link-default"
          onClick={onOpenCriteria}
        >
          {content.criteriaLink}
        </span>
      </Typo>
    </div>
  );
};
